import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Globe, Cpu, Loader2, RefreshCw, Zap, Save, Eye, EyeOff, CheckCircle2, XCircle, Trophy } from "lucide-react";
import AIModelCompare from "./AIModelCompare";
import AILLMHealth from "./AILLMHealth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { aiApi } from "./aiApi";

const CUSTOM = "__custom__";

export default function AILLMSettings() {
  const [cfg, setCfg] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [model, setModel] = useState("");
  const [custom, setCustom] = useState("");
  const [models, setModels] = useState([]);
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [prov, setProv] = useState("");
  const [fmt, setFmt] = useState("chat");

  const load = async () => {
    const c = await aiApi.llmConfig();
    setCfg(c); setBaseUrl(c.base_url); setModel(c.model); setApiKey(""); setFmt(c.api_format || "chat"); setProv(c.active_provider in (c.providers || {}) ? c.active_provider : "");
    return c;
  };
  const fetchModels = async (silent, over) => {
    setBusy("models");
    const q = over || { base_url: baseUrl || undefined, api_key: apiKey || undefined, provider: prov || undefined, api_format: fmt };
    try {
      const r = await aiApi.llmModels(q);
      setModels(r.models || []);
      if (!silent) toast.success(`${r.models.length} model dijumpai`);
    } catch (e) { if (!silent) toast.error(e.response?.data?.detail || "Gagal dapatkan model"); setModels([]); }
    finally { setBusy(""); }
  };
  useEffect(() => {
    load().then((c) => {
      const p = c.active_provider in (c.providers || {}) ? c.active_provider : undefined;
      return fetchModels(true, { base_url: c.base_url, provider: p, api_format: c.api_format || "chat" });
    }).catch(() => toast.error("Gagal memuat tetapan model")); // eslint-disable-next-line
  }, []);

  const chosen = model === CUSTOM ? custom.trim() : model;
  const payload = () => ({ base_url: baseUrl.trim() || undefined, api_key: apiKey.trim() || undefined, model: chosen || undefined, provider: prov || undefined, api_format: fmt });

  const pickProvider = async (p) => {
    const pr = cfg.providers[p];
    setProv(p); setBaseUrl(pr.base_url); setModel(pr.model); setFmt(pr.api_format || "chat"); setApiKey(""); setModels([]); setTest(null);
    if (pr.has_key) fetchModels(true, { base_url: pr.base_url, provider: p, api_format: pr.api_format || "chat" });
    if (p === cfg.active_provider) return;
    if (!pr.has_key) return toast.info(`Tampal API key ${pr.label} kemudian tekan Simpan untuk mengaktifkan.`);
    setBusy("switch");
    try { const c = await aiApi.saveLlmConfig({ provider: p }); setCfg(c); setModel(c.model); toast.success(`Penyedia aktif: ${pr.label} (${c.model})`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menukar penyedia"); } finally { setBusy(""); }
  };

  const runTest = async () => {
    setBusy("test"); setTest(null);
    try { setTest(await aiApi.llmTest(payload())); } catch (e) { setTest({ ok: false, error: e.response?.data?.detail || "Gagal" }); }
    finally { setBusy(""); }
  };
  const save = async () => {
    if (model === CUSTOM && !custom.trim()) return toast.error("Sila taip nama model");
    setBusy("save");
    try { const c = await aiApi.saveLlmConfig(payload()); setCfg(c); setApiKey(""); setModel(c.model); toast.success(`Disimpan — model aktif: ${c.model}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(""); }
  };

  const useModel = async (m) => {
    try { const c = await aiApi.saveLlmConfig({ model: m }); setCfg(c); setModel(c.model); toast.success(`Model aktif ditukar: ${c.model}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menukar model"); }
  };

  if (!cfg) return <div className="grid place-items-center h-24"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const options = Array.from(new Set([...(models || []), cfg.model].filter(Boolean)));

  return (
    <div className="rounded-2xl border border-fuchsia-100 bg-gradient-to-b from-fuchsia-50/60 to-white p-3 space-y-2.5" data-testid="ai-llm-settings">
      <div className="flex items-center gap-2">
        <div className="font-extrabold text-sm text-slate-800 flex-1">Tetapan Model AI</div>
        <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-white border border-fuchsia-200 text-fuchsia-700" data-testid="ai-llm-active-model">Aktif: {cfg.model}</span>
      </div>
      <div className="flex items-center gap-1.5" data-testid="ai-llm-provider-switch">
        <span className="text-[11px] font-bold text-slate-500">Penyedia</span>
        {Object.entries(cfg.providers || {}).map(([k, p]) => (
          <button key={k} type="button" onClick={() => pickProvider(k)} disabled={!!busy} data-testid={`ai-llm-provider-${k}`}
            className={`h-7 px-3 rounded-full text-[11px] font-bold border transition-colors ${prov === k ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"}`}>
            {busy === "switch" && prov === k && <Loader2 className="inline h-3 w-3 mr-1 animate-spin" />}{p.label}{cfg.active_provider === k ? " ✓" : ""}{!p.has_key ? " (tiada key)" : ""}
          </button>
        ))}
      </div>
      <AILLMHealth providers={cfg.providers} />
      <label className="block">
        <span className="flex items-center gap-1 text-[11px] font-bold text-slate-500 mb-1"><Globe className="h-3 w-3" /> Base URL (OpenAI-compatible)</span>
        <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.contoh.com/v1" className="h-8 text-xs bg-white" data-testid="ai-llm-base-url" />
      </label>
      <label className="block">
        <span className="flex items-center gap-1 text-[11px] font-bold text-slate-500 mb-1"><KeyRound className="h-3 w-3" /> API Key <span className="font-normal text-slate-400">(semasa: {(prov ? cfg.providers[prov]?.api_key_masked : cfg.api_key_masked) || "tiada"})</span></span>
        <div className="relative">
          <Input type={showKey ? "text" : "password"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Biarkan kosong untuk kekalkan key semasa"
            className="h-8 text-xs bg-white pr-8" autoComplete="off" data-testid="ai-llm-api-key" />
          <button type="button" onClick={() => setShowKey((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
      </label>
      <div className="flex items-center gap-1.5" data-testid="ai-llm-format">
        <span className="text-[11px] font-bold text-slate-500">Format API</span>
        {[["chat", "Chat Completions"], ["responses", "Responses"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFmt(k)} data-testid={`ai-llm-format-${k}`}
            className={`h-7 px-3 rounded-full text-[11px] font-bold border transition-colors ${fmt === k ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <span className="text-[10px] text-slate-400">{fmt === "responses" ? "/v1/responses" : "/v1/chat/completions"}</span>
      </div>
      <div>
        <span className="flex items-center gap-1 text-[11px] font-bold text-slate-500 mb-1"><Cpu className="h-3 w-3" /> Model
          <span className="font-normal text-slate-400" data-testid="ai-llm-models-count">{busy === "models" ? "(memuat senarai...)" : models.length ? `(${models.length} model tersedia)` : "(tekan ↻ untuk muat senarai)"}</span></span>
        <div className="flex gap-1.5">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="h-8 text-xs bg-white flex-1" data-testid="ai-llm-model-select"><SelectValue placeholder="Pilih model" /></SelectTrigger>
            <SelectContent className="z-[10005] max-h-64">
              {options.map((m) => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}
              <SelectItem value={CUSTOM} className="text-xs font-semibold">Lain-lain (taip sendiri)…</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs bg-white" onClick={() => fetchModels(false)} disabled={busy === "models"} data-testid="ai-llm-fetch-models" title="Muat semula senarai model">
            {busy === "models" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
        {model === CUSTOM && <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="cth: gpt-4o-mini" className="h-8 text-xs bg-white mt-1.5" data-testid="ai-llm-custom-model" />}
      </div>
      {test && (
        <div className={`flex items-start gap-1.5 rounded-xl px-2.5 py-2 text-[11px] ${test.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="ai-llm-test-result">
          {test.ok ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
          <span>{test.ok ? `Berjaya (${test.model}, ${test.latency_ms} ms): “${test.reply}”` : `Gagal: ${test.error}`}</span>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={runTest} disabled={!!busy} className="flex-1 h-8 rounded-xl text-xs bg-white" data-testid="ai-llm-test-btn">
          {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Zap className="h-3.5 w-3.5 mr-1" />} Uji Sambungan
        </Button>
        <Button size="sm" onClick={save} disabled={!!busy} className="flex-1 h-8 rounded-xl text-xs bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="ai-llm-save-btn">
          {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Simpan
        </Button>
      </div>
      <Button size="sm" variant="outline" onClick={() => setCompareOpen(true)} className="w-full h-8 rounded-xl text-xs bg-white border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50" data-testid="ai-llm-compare-btn">
        <Trophy className="h-3.5 w-3.5 mr-1" /> Bandingkan Model
      </Button>
      <AIModelCompare open={compareOpen} onOpenChange={setCompareOpen} models={models} activeModel={cfg.model} onUse={useModel} />
      <div className="text-[10px] text-slate-400">Perubahan berkuat kuasa serta-merta untuk Mimi Junior (semua peranan). {cfg.updated_by ? `Dikemas kini oleh ${cfg.updated_by}.` : "Kini menggunakan konfigurasi lalai pelayan."}</div>
    </div>
  );
}
